import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../utils/assets';
import { getContent } from '../../api/content';
import Loading from './Loading';
import './MeetingModal.css';

interface MeetingModalProps {
  visible: boolean;
  onClose: () => void;
}

const MeetingModal: React.FC<MeetingModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation(['game']);
  const [content, setContent] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [thumbTop, setThumbTop] = useState<string>('5.11rem'); // Initial top position from CSS
  const contentRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Helper to get image path
  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;

  // Constants for scrollbar (in rem)
  // These should match CSS or be calculated dynamically if possible, 
  // but hardcoding based on CSS is easier for this specific fixed layout.
  const TRACK_TOP_REM = 5.09;
  const TRACK_HEIGHT_REM = 5.14;
  const THUMB_HEIGHT_REM = 1.91;
  const INITIAL_THUMB_TOP_REM = 5.11;
  const MAX_THUMB_TRAVEL_REM = TRACK_HEIGHT_REM - THUMB_HEIGHT_REM - (INITIAL_THUMB_TOP_REM - TRACK_TOP_REM) * 2; 
  // Simplified: specific movement range based on visual design. 
  // Let's rely on scroll percentage mapping.
  // Track starts at 5.09. Height 5.14. Ends at 10.23.
  // Thumb starts at 5.11. Height 1.91. Ends at 7.02.
  // Thumb max top position: 5.11 + (5.14 - 1.91) roughly? 
  // Actually usually thumb moves within track. 
  // Available space for thumb movement = Track Height - Thumb Height.
  // 5.14 - 1.91 = 3.23 rem.
  
  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [visible]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getContent('lingshanhui');
      // Replace newline characters with <br/> tags for HTML rendering
      setContent((data.content || '').replace(/\n/g, '<br/>'));
      setTitle(data.title);
    } catch (error) {
      console.error('Failed to fetch meeting content', error);
      // Fallback to translation if API fails
      setContent(t('meeting.agreementContent').replace(/\n/g, '<br/>'));
      setTitle(t('meeting.title'));
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = () => {
    if (!contentRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const maxScroll = scrollHeight - clientHeight;
    
    if (maxScroll <= 0) {
        setThumbTop(`${INITIAL_THUMB_TOP_REM}rem`);
        return;
    }

    const scrollRatio = scrollTop / maxScroll;
    const maxThumbMovement = 3.23; // 5.14 - 1.91
    
    // Calculate new top
    const newTop = INITIAL_THUMB_TOP_REM + (scrollRatio * maxThumbMovement);
    setThumbTop(`${newTop}rem`);
  };

  return (
    <div className={`meeting-modal-container ${visible ? 'visible' : ''}`}>
      <div className="meeting-modal-backdrop" style={{ backgroundImage: getImg('popup-backdrop.png') }} onClick={onClose} />
      
      <div className="meeting-content-wrapper">
        <div className="gg-main-frame">
          {/* Background Layer */}
          <div className="gg-background-layer"/>
          
          {/* Content Panel Background */}
          <div className="gg-content-panel" style={{ backgroundImage: getImg('gg_content_panel.png') }} />
          
          {/* Close Button */}
          <div 
            className="gg-close-button" 
            style={{ backgroundImage: getImg('gg_close_button.png') }} 
            onClick={onClose}
          />
          
          {/* Title */}
          <span className="gg-title-text">{title || t('meeting.title')}</span>
          
          {/* Confirm Button */}
          <div 
            className="gg-confirm-button" 
            style={{ backgroundImage: getImg('gg_button_primary.png') }}
            onClick={onClose} // Or handle confirm action
          />
          <span className="gg-confirm-text">{t('common.confirm')}</span>
          
          {/* Cancel Button */}
          <div 
            className="gg-cancel-button" 
            style={{ backgroundImage: getImg('gg_button_primary.png') }}
            onClick={onClose}
          />
          <span className="gg-cancel-text">{t('common.cancel')}</span>
          
          {/* Agreement Box Background */}
          <div className="gg-agreement-box" style={{ backgroundImage: getImg('gg_agreement_box.png') }} />
          
          {/* Agreement Content */}
          <div 
            className="gg-agreement-content" 
            ref={contentRef}
            onScroll={handleScroll}
            style={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
          
          {/* Prompt Text */}
          <span className="gg-prompt-text" dangerouslySetInnerHTML={{ __html: `"${t('meeting.prompt')}"` }} />
          
          {/* Custom Scrollbar */}
          <div 
            className="gg-scrollbar-track" 
            ref={trackRef}
            style={{ backgroundImage: getImg('gg_scrollbar_track.png') }} 
          />
          <div 
            className="gg-scrollbar-thumb" 
            style={{ 
              backgroundImage: getImg('gg_scrollbar_thumb.png'),
              top: thumbTop
            }} 
          />
          
          {/* DAO Label */}
          <span className="gg-dao-label">{t('meeting.daoLabel')}</span>
          
          {/* Title Decoration */}
          <div className="gg-title-decoration" style={{ backgroundImage: getImg('gg_title_decoration.png') }} />
          
          {/* Disabled Button (Example usage if needed, otherwise hidden or conditional) */}
          {/* <div className="gg-disabled-button" style={{ backgroundImage: getImg('gg_button_disabled.png') }} /> */}
          
          {loading && <Loading />}
        </div>
      </div>
    </div>
  );
};

export default MeetingModal;
